/**
 * Meeting Preparation API (API v2)
 * 3-step async flow: Submit → Pooling → Retrieve Expressions
 */

import apiClient from "../apiClient";
import {
  PreparationSubmission,
  SubmitPreparationResponse,
  PoolingStatusResponse,
  PreparedExpression,
} from "@/types/meeting-preparation.types";
import { logger } from "@/utils/logger";

/**
 * STEP 1: Submit preparation answers
 * POST /teacher/meeting/preparation/submit
 * Returns need_pooling=true to indicate async processing
 * Note: BASE_URL already includes /api/v2/
 */
export const submitPreparation = async (
  submission: PreparationSubmission
): Promise<SubmitPreparationResponse> => {
  logger.log(
    "[submitPreparation] Submitting to /teacher/meeting/preparation/submit"
  );
  logger.log("[submitPreparation] Payload module_id:", submission.module_id);
  logger.log(
    "[submitPreparation] Payload answers count:",
    submission.answers.length
  );
  logger.log(
    "[submitPreparation] Payload answers:",
    JSON.stringify(submission.answers, null, 2)
  );

  try {
    const response = await apiClient.post<any>(
      "/teacher/meeting/preparation/submit",
      submission
    );

    logger.log("[submitPreparation] Raw response:", response);

    // Handle different response formats
    // Backend might return: { data: { need_pooling: true } } or { need_pooling: true }
    const data = response.data || response;

    logger.log("[submitPreparation] Extracted data:", data);

    if (!data.need_pooling) {
      logger.warn(
        "[submitPreparation] need_pooling is false or missing:",
        data
      );
    }

    return {
      need_pooling: data.need_pooling || false,
      message: data.message || "Submitted successfully",
    };
  } catch (error: any) {
    logger.error("[submitPreparation] Error:", error);
    logger.error("[submitPreparation] Error response:", error?.response);
    logger.error(
      "[submitPreparation] Error response data:",
      error?.response?.data
    );
    logger.error(
      "[submitPreparation] Error response status:",
      error?.response?.status
    );
    logger.error("[submitPreparation] Error message:", error?.message);
    throw error;
  }
};

/**
 * STEP 2: Check pooling status
 * GET /teacher/meeting/preparation/pooling/{realtime_expression_id}/{meeting_session}
 * Returns finished=true when processing is complete
 * Note: BASE_URL already includes /api/v2/
 */
export const checkPoolingStatus = async (
  moduleId: string,
  meetingSession: string
): Promise<PoolingStatusResponse> => {
  try {
    logger.log(
      "[checkPoolingStatus] Checking status for:",
      moduleId,
      "session:",
      meetingSession
    );
    const response = await apiClient.get<any>(
      `/teacher/meeting/preparation/pooling/${moduleId}/${meetingSession}`
    );
    logger.log("[checkPoolingStatus] Raw response:", response);

    // Handle different response formats
    const data = response.data || response;

    return {
      finished: data.finished || false,
    };
  } catch (error: any) {
    logger.error("[checkPoolingStatus] Error:", error);
    throw error;
  }
};

/**
 * STEP 3: Fetch generated expressions with audio
 * GET /teacher/meeting/preparation/expressions/{realtime_expression_id}/{meeting_session}
 * Returns array of expressions with audio URLs
 * Note: BASE_URL already includes /api/v2/
 */
export const fetchPreparedExpressions = async (
  moduleId: string,
  meetingSession: string
): Promise<PreparedExpression[]> => {
  try {
    logger.log(
      "[fetchPreparedExpressions] Fetching expressions for module:",
      moduleId,
      "session:",
      meetingSession
    );
    const response = await apiClient.get<any>(
      `/teacher/meeting/preparation/expressions/${moduleId}/${meetingSession}`
    );
    logger.log("[fetchPreparedExpressions] Raw response:", response);

    // Handle different response formats
    const data = response.data || response;
    const expressions = Array.isArray(data) ? data : data.expressions || [];

    logger.log(
      "[fetchPreparedExpressions] Extracted expressions:",
      expressions
    );

    return expressions;
  } catch (error: any) {
    logger.error("[fetchPreparedExpressions] Error:", error);
  }
};

/**
 * Fetch top expressions from dashboard session
 * GET /teacher/meeting/top-expression/{meeting_session}
 * Returns array of expressions with audio URLs
 * Note: BASE_URL already includes /api/v2/
 */
export const fetchTopExpressions = async (
  meetingSession: string
): Promise<PreparedExpression[]> => {
  try {
    logger.log(
      "[fetchTopExpressions] Fetching top expressions for session:",
      meetingSession
    );
    const response = await apiClient.get<any>(
      `/teacher/meeting/top-expression/${meetingSession}`
    );
    logger.log("[fetchTopExpressions] Raw response:", response);

    // Handle different response formats
    const data = response.data || response;
    const expressions = Array.isArray(data) ? data : data.expressions || [];

    logger.log(
      "[fetchTopExpressions] Extracted expressions:",
      expressions.length,
      "items"
    );

    return expressions;
  } catch (error: any) {
    logger.error("[fetchTopExpressions] Error:", error);
    throw error;
  }
};
